import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useFleetAuth } from "../../auth/fleetAuth";
import { Content, FullPageContainer, Right } from "../Dashboard/Dashboard";
import Navbar from "../../components/Navbar/Navbar";
import PageHeader from "../../components/PageHeader/PageHeader";
import Footer from "../../components/Footer/Footer";
import ViewSwitcher from "../../components/ViewSwitcher/ViewSwitcher";
import {
  AirlineCode,
  AutoRefreshGroup,
  CheckboxRow,
  ChipContainer,
  ColumnDef,
  ConcourseChip,
  ConcourseChipRow,
  CountBadge,
  EmptyMessage,
  ErrorBanner,
  FilterCard,
  SortDir,
  FilterCheckbox,
  FilterForm,
  FilterGroup,
  FilterInput,
  FilterLabel,
  FilterSelect,
  FlightCell,
  FlightTableRow,
  LoadingText,
  MonoText,
  SearchButton,
  SortableHeaderCell,
  Table,
  TableBody,
  TableHeader,
  TableScrollWrapper,
  TableTitle,
  TimeTypeBadge,
  RelativeText,
  HighlightCell,
  ChangeDot,
  DOT_COLORS,
  AIRPORT_GATE_GROUPS,
  normalizeGate,
  formatDateTime,
  formatTime,
  parsePierFilter,
  relativeTime,
  timeTypeLabel,
  useColumnVisibility,
  useSort,
} from "../../components/FlightDataTable";
import type { HighlightKind } from "../../components/FlightDataTable";
import { fetchFlights } from "./api";
import { Flight, FlightFilters, FlightsResponse } from "./types";

function allConcourseLabels(airport: string): ReadonlySet<string> {
  const groups = AIRPORT_GATE_GROUPS[airport.toUpperCase()] ?? [];
  return new Set(groups.map((g) => g.label));
}

const DEFAULT_FILTERS: FlightFilters = {
  airport: "ATL",
  hoursBack: 0,
  hoursForward: 9,
  missionType: "OUTBOUND",
  requireBagPier: true,
  selectedConcourses: allConcourseLabels("ATL"),
  pierFilter: "40-60",
};

const AUTO_REFRESH_MS = 60_000;
const ONE_HOUR_MS = 60 * 60 * 1000;

const ALL_COLUMNS: readonly ColumnDef<ColumnKey, SortKey>[] = [
  { key: "flight", label: "Flight", sortKey: "flight" },
  { key: "destination", label: "Destination", sortKey: "destination" },
  { key: "date", label: "Date", sortKey: "date" },
  { key: "time", label: "Time", sortKey: "time" },
  { key: "timeType", label: "Status", sortKey: "timeType" },
  { key: "relative", label: "Relative", sortKey: null },
  { key: "gate", label: "Gate", sortKey: "gate" },
  { key: "bagPier", label: "Pier", sortKey: "bagPier" },
  { key: "aircraft", label: "Aircraft", sortKey: "aircraft" },
];

const DEFAULT_VISIBLE_COLUMNS: ReadonlySet<ColumnKey> = new Set<ColumnKey>([
  "flight",
  "destination",
  "time",
  "timeType",
  "relative",
  "gate",
  "bagPier",
]);

function fieldUpdatedWithin(
  flight: Flight,
  fieldName: string,
  windowMs: number,
  now: number,
): boolean {
  const ts = flight.field_updated_at?.[fieldName];
  if (ts == null) return false;
  return ts > now - windowMs;
}

type ColumnKey =
  | "flight"
  | "destination"
  | "date"
  | "time"
  | "timeType"
  | "relative"
  | "gate"
  | "bagPier"
  | "aircraft";

type SortKey =
  | "flight"
  | "destination"
  | "date"
  | "time"
  | "timeType"
  | "gate"
  | "bagPier"
  | "aircraft";

function getFieldValue(
  flight: Flight,
  key: SortKey,
  type: "INBOUND" | "OUTBOUND",
): string | number {
  switch (key) {
    case "flight":
      return `${flight.al_cde}${String(flight.flt_num).padStart(6, "0")}`;
    case "destination":
      return type === "INBOUND"
        ? flight.leg_orig_ap_cde
        : flight.leg_dest_ap_cde;
    case "date":
    case "time":
      return flight.mission_time;
    case "gate":
      return (type === "INBOUND" ? flight.arvl_gate : flight.dptr_gate) ?? "";
    case "bagPier":
      return (
        (type === "INBOUND"
          ? flight.arvl_bag_pier_num
          : flight.dptr_bag_pier_num) ?? ""
      );
    case "timeType":
      return flight.time_type;
    case "aircraft":
      return flight.ac_reg_num ?? "";
  }
}

function sortFlights(
  flights: readonly Flight[],
  sortKey: SortKey,
  sortDir: SortDir,
  type: "INBOUND" | "OUTBOUND",
): Flight[] {
  return [...flights].sort((a, b) => {
    const aVal = getFieldValue(a, sortKey, type);
    const bVal = getFieldValue(b, sortKey, type);

    const cmp =
      typeof aVal === "number" && typeof bVal === "number"
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));

    return sortDir === "asc" ? cmp : -cmp;
  });
}

const Flights = () => {
  const { getAccessToken } = useFleetAuth();
  const [filters, setFilters] = useState<FlightFilters>(DEFAULT_FILTERS);
  const [draft, setDraft] = useState<FlightFilters>(DEFAULT_FILTERS);
  const [data, setData] = useState<FlightsResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const inboundSort = useSort<SortKey>("time");
  const outboundSort = useSort<SortKey>("time");

  const { isVisible, toggle: handleColumnToggle } =
    useColumnVisibility<ColumnKey>(DEFAULT_VISIBLE_COLUMNS);

  const loadFlights = useCallback(
    async (f: FlightFilters) => {
      if (abortRef.current) {
        abortRef.current.abort();
      }
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      setError(null);
      try {
        const token = await getAccessToken();
        const result = await fetchFlights(f, token, controller.signal);
        if (!controller.signal.aborted) {
          setData(result);
        }
      } catch (err) {
        if (controller.signal.aborted) return;
        const message =
          err instanceof Error ? err.message : "Failed to fetch flights";
        setError(message);
      } finally {
        if (!controller.signal.aborted) {
          setLoading(false);
        }
      }
    },
    [getAccessToken],
  );

  useEffect(() => {
    loadFlights(filters);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    if (autoRefresh) {
      intervalRef.current = setInterval(() => {
        loadFlights(filters);
      }, AUTO_REFRESH_MS);
    }
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [autoRefresh, filters, loadFlights]);

  const handleApply = (e: React.FormEvent) => {
    e.preventDefault();
    const airportChanged = draft.airport !== filters.airport;
    const newFilters: FlightFilters = airportChanged
      ? { ...draft, selectedConcourses: allConcourseLabels(draft.airport) }
      : { ...draft };
    setFilters(newFilters);
    setDraft(newFilters);
    loadFlights(newFilters);
  };

  const updateDraft = <K extends keyof FlightFilters>(
    key: K,
    value: FlightFilters[K],
  ) => {
    setDraft((prev) => ({ ...prev, [key]: value }));
  };

  const handleConcourseToggle = (label: string) => {
    const toggle = (prev: FlightFilters): FlightFilters => {
      const next = new Set(prev.selectedConcourses);
      if (next.has(label)) {
        next.delete(label);
      } else {
        next.add(label);
      }
      return { ...prev, selectedConcourses: next };
    };
    setFilters(toggle);
    setDraft(toggle);
  };

  const currentGateGroups = useMemo(
    () => AIRPORT_GATE_GROUPS[filters.airport.toUpperCase()] ?? [],
    [filters.airport],
  );

  const allowedGates = useMemo(() => {
    if (filters.selectedConcourses.size === 0) return new Set<string>();
    const gates = new Set<string>();
    for (const group of currentGateGroups) {
      if (filters.selectedConcourses.has(group.label)) {
        for (const gate of group.gates) {
          gates.add(gate);
        }
      }
    }
    return gates;
  }, [filters.selectedConcourses, currentGateGroups]);

  const allowedPiers = useMemo(
    () => parsePierFilter(filters.pierFilter),
    [filters.pierFilter],
  );

  const filteredInbound = useMemo(() => {
    if (!data) return [];
    return data.inbound.filter((f) => {
      if (allowedGates.size > 0) {
        if (!allowedGates.has(normalizeGate((f.arvl_gate ?? "").toUpperCase())))
          return false;
      }
      if (allowedPiers.size > 0) {
        if (!allowedPiers.has(f.arvl_bag_pier_num ?? "")) return false;
      }
      return true;
    });
  }, [data, allowedGates, allowedPiers]);

  const filteredOutbound = useMemo(() => {
    if (!data) return [];
    return data.outbound.filter((f) => {
      if (allowedGates.size > 0) {
        if (!allowedGates.has(normalizeGate((f.dptr_gate ?? "").toUpperCase())))
          return false;
      }
      if (allowedPiers.size > 0) {
        if (!allowedPiers.has(f.dptr_bag_pier_num ?? "")) return false;
      }
      return true;
    });
  }, [data, allowedGates, allowedPiers]);

  const sortedInbound = useMemo(
    () =>
      sortFlights(
        filteredInbound,
        inboundSort.sortKey,
        inboundSort.sortDir,
        "INBOUND",
      ),
    [filteredInbound, inboundSort.sortKey, inboundSort.sortDir],
  );

  const sortedOutbound = useMemo(
    () =>
      sortFlights(
        filteredOutbound,
        outboundSort.sortKey,
        outboundSort.sortDir,
        "OUTBOUND",
      ),
    [filteredOutbound, outboundSort.sortKey, outboundSort.sortDir],
  );

  const activeColumns = ALL_COLUMNS.filter((col) => isVisible(col.key));

  const inboundColumns = activeColumns.map((col) =>
    col.key === "destination" ? { ...col, label: "From" } : col,
  );

  const outboundColumns = activeColumns.map((col) =>
    col.key === "destination" ? { ...col, label: "To" } : col,
  );

  const now = Date.now();

  const renderFlightRow = (flight: Flight, type: "INBOUND" | "OUTBOUND") => {
    const isPast = flight.mission_time < now;
    const destination =
      type === "INBOUND" ? flight.leg_orig_ap_cde : flight.leg_dest_ap_cde;
    const gate = type === "INBOUND" ? flight.arvl_gate : flight.dptr_gate;
    const bagPier =
      type === "INBOUND" ? flight.arvl_bag_pier_num : flight.dptr_bag_pier_num;

    const gateField = type === "INBOUND" ? "arvl_gate" : "dptr_gate";
    const gateChanged = fieldUpdatedWithin(flight, gateField, ONE_HOUR_MS, now);
    const timeChanged =
      fieldUpdatedWithin(flight, "mission_time", ONE_HOUR_MS, now) ||
      fieldUpdatedWithin(flight, "time_type", ONE_HOUR_MS, now);

    const changeDots: Exclude<HighlightKind, "none">[] = [];
    if (timeChanged) changeDots.push("time");
    if (gateChanged) changeDots.push("gate");

    const dlCodeshare =
      flight.al_cde !== "DL"
        ? flight.codeshares?.find((cs) => cs.al_cde === "DL")
        : undefined;
    const displayAlCde = dlCodeshare?.al_cde ?? flight.al_cde;
    const displayFltNum = dlCodeshare?.flt_num ?? flight.flt_num;

    return (
      <FlightTableRow key={flight.SK} $isPast={isPast}>
        {isVisible("flight") && (
          <FlightCell>
            <AirlineCode>{displayAlCde}</AirlineCode>
            {displayFltNum}
            {changeDots.map((kind) => (
              <ChangeDot key={kind} $color={DOT_COLORS[kind]} />
            ))}
          </FlightCell>
        )}
        {isVisible("destination") && (
          <FlightCell>
            <strong>{destination}</strong>
          </FlightCell>
        )}
        {isVisible("date") && (
          <FlightCell>{formatDateTime(flight.mission_time)}</FlightCell>
        )}
        {isVisible("time") && (
          <HighlightCell $highlight={timeChanged ? "time" : "none"}>
            {formatTime(flight.mission_time)}
          </HighlightCell>
        )}
        {isVisible("timeType") && (
          <FlightCell>
            <TimeTypeBadge $type={flight.time_type}>
              {timeTypeLabel(flight.time_type)}
            </TimeTypeBadge>
          </FlightCell>
        )}
        {isVisible("relative") && (
          <FlightCell>
            <RelativeText $isPast={isPast}>
              {relativeTime(flight.mission_time)}
            </RelativeText>
          </FlightCell>
        )}
        {isVisible("gate") && (
          <HighlightCell $highlight={gateChanged ? "gate" : "none"}>
            {gate ?? "—"}
          </HighlightCell>
        )}
        {isVisible("bagPier") && <FlightCell>{bagPier ?? "—"}</FlightCell>}
        {isVisible("aircraft") && (
          <FlightCell>
            <MonoText>{flight.ac_reg_num ?? "—"}</MonoText>
          </FlightCell>
        )}
      </FlightTableRow>
    );
  };

  return (
    <FullPageContainer>
      <Navbar />
      <Right>
        <PageHeader>Flights</PageHeader>
        <Content>
          <ViewSwitcher />
          {/* Filters */}
          <FilterCard>
            <FilterForm onSubmit={handleApply}>
              <FilterGroup>
                <FilterLabel htmlFor="airport">Airport</FilterLabel>
                <FilterInput
                  id="airport"
                  value={draft.airport}
                  onChange={(e) =>
                    updateDraft("airport", e.target.value.toUpperCase())
                  }
                  placeholder="ATL"
                  maxLength={4}
                  required
                  style={{ width: "80px" }}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="hoursBack">Past Hours</FilterLabel>
                <FilterInput
                  id="hoursBack"
                  type="number"
                  min={0}
                  max={48}
                  value={draft.hoursBack}
                  onChange={(e) =>
                    updateDraft("hoursBack", Number(e.target.value))
                  }
                  style={{ width: "80px" }}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="hoursForward">Hours Fwd</FilterLabel>
                <FilterInput
                  id="hoursForward"
                  type="number"
                  min={0}
                  max={48}
                  value={draft.hoursForward}
                  onChange={(e) =>
                    updateDraft("hoursForward", Number(e.target.value))
                  }
                  style={{ width: "80px" }}
                />
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="missionType">Type</FilterLabel>
                <FilterSelect
                  id="missionType"
                  value={draft.missionType}
                  onChange={(e) =>
                    updateDraft(
                      "missionType",
                      e.target.value as FlightFilters["missionType"],
                    )
                  }
                >
                  <option value="ALL">All</option>
                  <option value="INBOUND">Inbound</option>
                  <option value="OUTBOUND">Outbound</option>
                </FilterSelect>
              </FilterGroup>
              <FilterGroup>
                <CheckboxRow>
                  <FilterCheckbox
                    id="requireBagPier"
                    type="checkbox"
                    checked={draft.requireBagPier}
                    onChange={(e) =>
                      updateDraft("requireBagPier", e.target.checked)
                    }
                  />
                  <FilterLabel
                    htmlFor="requireBagPier"
                    style={{ marginBottom: 0 }}
                  >
                    Bag Pier
                  </FilterLabel>
                </CheckboxRow>
              </FilterGroup>
              <FilterGroup>
                <FilterLabel htmlFor="pierFilter">Piers</FilterLabel>
                <FilterInput
                  id="pierFilter"
                  value={draft.pierFilter}
                  onChange={(e) => updateDraft("pierFilter", e.target.value)}
                  placeholder="e.g. 40-60, 72"
                  style={{ width: "130px" }}
                />
              </FilterGroup>
              <SearchButton
                type="submit"
                disabled={loading || !draft.airport.trim()}
              >
                {loading ? "Loading..." : "Search"}
              </SearchButton>
              <AutoRefreshGroup>
                <FilterCheckbox
                  id="autoRefresh"
                  type="checkbox"
                  checked={autoRefresh}
                  onChange={(e) => setAutoRefresh(e.target.checked)}
                />
                <FilterLabel htmlFor="autoRefresh" style={{ marginBottom: 0 }}>
                  Auto-refresh
                </FilterLabel>
              </AutoRefreshGroup>
            </FilterForm>

            {/* Concourse Chips */}
            {currentGateGroups.length > 0 && (
              <ConcourseChipRow>
                <FilterLabel as="span">Concourse</FilterLabel>
                <ChipContainer>
                  {currentGateGroups.map((group) => (
                    <ConcourseChip
                      key={group.label}
                      type="button"
                      $active={filters.selectedConcourses.has(group.label)}
                      onClick={() => handleConcourseToggle(group.label)}
                    >
                      {group.label}
                    </ConcourseChip>
                  ))}
                </ChipContainer>
              </ConcourseChipRow>
            )}

            {/* Column Toggles */}
            <ConcourseChipRow>
              <FilterLabel as="span">Columns</FilterLabel>
              <ChipContainer>
                {ALL_COLUMNS.map((col) => (
                  <ConcourseChip
                    key={col.key}
                    type="button"
                    $active={isVisible(col.key)}
                    onClick={() => handleColumnToggle(col.key)}
                  >
                    {col.label}
                  </ConcourseChip>
                ))}
              </ChipContainer>
            </ConcourseChipRow>
          </FilterCard>

          {/* Error */}
          {error && <ErrorBanner>{error}</ErrorBanner>}

          {/* Loading */}
          {loading && !data && <LoadingText>Loading flights...</LoadingText>}

          {/* Inbound Table */}
          {data &&
            (filters.missionType === "ALL" ||
              filters.missionType === "INBOUND") && (
              <Table>
                <TableTitle>
                  Inbound Flights
                  <CountBadge>{filteredInbound.length}</CountBadge>
                </TableTitle>
                {filteredInbound.length === 0 ? (
                  <EmptyMessage>No inbound flights found.</EmptyMessage>
                ) : (
                  <TableScrollWrapper>
                    <TableBody>
                      <TableHeader>
                        {inboundColumns.map((col) => (
                          <SortableHeaderCell
                            key={col.key}
                            $sortable={col.sortKey !== null}
                            onClick={
                              col.sortKey
                                ? () => inboundSort.setSort(col.sortKey!)
                                : undefined
                            }
                          >
                            {col.label}
                            {inboundSort.indicator(col)}
                          </SortableHeaderCell>
                        ))}
                      </TableHeader>
                      {sortedInbound.map((flight) =>
                        renderFlightRow(flight, "INBOUND"),
                      )}
                    </TableBody>
                  </TableScrollWrapper>
                )}
              </Table>
            )}

          {/* Outbound Table */}
          {data &&
            (filters.missionType === "ALL" ||
              filters.missionType === "OUTBOUND") && (
              <Table>
                <TableTitle>
                  Outbound Flights
                  <CountBadge>{filteredOutbound.length}</CountBadge>
                </TableTitle>
                {filteredOutbound.length === 0 ? (
                  <EmptyMessage>No outbound flights found.</EmptyMessage>
                ) : (
                  <TableScrollWrapper>
                    <TableBody>
                      <TableHeader>
                        {outboundColumns.map((col) => (
                          <SortableHeaderCell
                            key={col.key}
                            $sortable={col.sortKey !== null}
                            onClick={
                              col.sortKey
                                ? () => outboundSort.setSort(col.sortKey!)
                                : undefined
                            }
                          >
                            {col.label}
                            {outboundSort.indicator(col)}
                          </SortableHeaderCell>
                        ))}
                      </TableHeader>
                      {sortedOutbound.map((flight) =>
                        renderFlightRow(flight, "OUTBOUND"),
                      )}
                    </TableBody>
                  </TableScrollWrapper>
                )}
              </Table>
            )}
        </Content>
        <Footer />
      </Right>
    </FullPageContainer>
  );
};

/* ── Page-specific styled components ──────────────────────────────────
 * Generic chrome (FilterCard, Table, FlightTableRow, etc.) lives in
 * src/components/FlightDataTable. The pieces below — change-highlights,
 * dot indicators, the Status / Relative-time text — are flight-specific
 * and stay here.
 * ─────────────────────────────────────────────────────────────────── */

export default Flights;
